// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useRef, useState } from "react";
import type { MutableRefObject, FunctionComponent } from "react";
import { t } from "i18next";
import Cookies from "js-cookie";
import {
  DropdownPanel,
  PlainTextField,
  RoundButton,
  EmptyButton,
  roundButtonStylesMedium,
  Link,
} from "@smartsuite/react-ui/lib";

import { Root, SettingsDropdownBody } from "./style";
import type { SolutionColor } from "@smartsuite/types/lib/constants/color";
import { getLinkToTable, getSmartSuiteLink } from "./helpers";

export interface SetupSettings {
  accountId: string;
  solutionId: string;
  engineUrl: string;
  limitsUrl: string;
  accessToken: string;
  lang: string;
  logoColor: SolutionColor;
  smartsuiteBackendUrl: string;
}

export const SetupDropdown: FunctionComponent<{
  value: SetupSettings;
  setValue: (settings: SetupSettings) => void;
  openAutomation: () => void;
}> = ({ value, setValue, openAutomation }) => {
  const rootRef = useRef(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [localValue, setLocalValue] = useState<SetupSettings>(value);

  const onSubmit = (): void => {
    setModalOpen(false);
    Cookies.set("accessToken", localValue.accessToken, {
      domain: ".smartsuite.com",
    });
    if (
      value.accountId !== localValue.accountId ||
      value.accessToken !== localValue.accessToken ||
      value.solutionId !== localValue.solutionId ||
      value.engineUrl !== localValue.engineUrl ||
      value.limitsUrl !== localValue.engineUrl ||
      value.lang !== localValue.lang ||
      value.smartsuiteBackendUrl !== localValue.smartsuiteBackendUrl
    ) {
      setValue(localValue);
    }
  };

  return (
    <Root ref={rootRef} isModalOpen={isModalOpen} mainColor="black">
      <div className="head" style={{ padding: 5 }}>
        <RoundButton
          ref={buttonRef}
          iconName="settings"
          styles={{
            ...roundButtonStylesMedium,
            ...{ height: 22, sidePadding: 10 },
          }}
          onClick={() => setModalOpen((isOpen) => !isOpen)}
        >
          {t("setup-dropdown.settings") ?? "Settings"}
        </RoundButton>
      </div>

      <DropdownPanel
        targetElementRef={buttonRef as MutableRefObject<HTMLElement>}
        my="left top"
        at="left bottom+11"
        arrowBackground="white"
        isActive={isModalOpen}
        minWidth={300}
        onClickOutside={() => setModalOpen(false)}
        zIndex={1002}
      >
        <SettingsDropdownBody>
          <Link
            className="link"
            href={getLinkToTable(
              getSmartSuiteLink(localValue.smartsuiteBackendUrl),
              localValue.accountId,
              localValue.solutionId,
            )}
            target="_blank"
            rel="noopener noreferrer"
            weight="medium"
          >
            Link to SmartSuite table
          </Link>

          <PlainTextField
            label="Account ID"
            name="account-id"
            placeholder=""
            value={localValue.accountId}
            onChange={(e) =>
              setLocalValue({ ...localValue, accountId: e.target.value })
            }
          />

          <PlainTextField
            label="Solution ID"
            name="solution-id"
            placeholder=""
            value={localValue.solutionId}
            onChange={(e) =>
              setLocalValue({ ...localValue, solutionId: e.target.value })
            }
          />

          <PlainTextField
            label="Access Token"
            name="access-token"
            placeholder=""
            value={localValue.accessToken}
            onChange={(e) =>
              setLocalValue({ ...localValue, accessToken: e.target.value })
            }
          />

          <PlainTextField
            label="Engine URL"
            name="engine-url"
            placeholder=""
            value={localValue.engineUrl}
            onChange={(e) =>
              setLocalValue({ ...localValue, engineUrl: e.target.value })
            }
          />
          <PlainTextField
            label="Limits URL"
            name="limits-url"
            placeholder=""
            value={localValue.limitsUrl}
            onChange={(e) =>
              setLocalValue({ ...localValue, limitsUrl: e.target.value })
            }
          />
          <PlainTextField
            label="SmartSuite backend URL"
            name="smartsuite-backend-url"
            placeholder=""
            value={localValue.smartsuiteBackendUrl}
            onChange={(e) =>
              setLocalValue({
                ...localValue,
                smartsuiteBackendUrl: e.target.value,
              })
            }
          />

          <PlainTextField
            label="Language"
            name="lang"
            placeholder=""
            value={localValue.lang}
            onChange={(e) =>
              setLocalValue({ ...localValue, lang: e.target.value })
            }
          />

          <div className="footer" style={{ gap: "5px" }}>
            <RoundButton
              styles={{ ...roundButtonStylesMedium, ...{ sidePadding: 35 } }}
              onClick={() => onSubmit()}
            >
              {t("general.save")}
            </RoundButton>

            <EmptyButton
              size="lg"
              onClick={() => {
                openAutomation();
                setModalOpen(false);
              }}
            >
              Open Automations
            </EmptyButton>
          </div>
        </SettingsDropdownBody>
      </DropdownPanel>
    </Root>
  );
};
